/* sugar:assets/gift-list.js@2.1.2 */
/*
 * gift-list — gifts that come free with a product.
 *
 * Serves Gift List and Gift Grid ([data-gift-list] with [data-gift-item] children), Featured Gift
 * ([data-featured-gift]) and the gift markers a Perk or a Subscriptions card renders
 * ([data-subscription-gift], and Offer Gift rows inside [data-sugar-subscriptions]).
 *
 * Each of those is a provider: it carries data-cart-extras and exposes sugarExtras, so its gifts
 * ride along with whichever buy button submits, through SugarCart.extras. Nothing here
 * intercepts a request. A gift inside an Offer only rides while that offer is the selected tier;
 * a subscription gift, or any gift block set inside a Subscriptions card, only while the section
 * is set to subscribe.
 *
 * Every line carries _gift_list = 'true' and _gift_for_product = the product it came with, which
 * is how a later cart change finds a gift whose product has left the cart and takes it out too.
 */
(function () {
  'use strict';
  if (window.SugarGiftList) return;

  const PROVIDERS = '[data-gift-list], [data-featured-gift], [data-subscription-gift], [data-sugar-subscriptions] .sugar-offer-gift';
  const PROP = '_gift_list';
  const FOR_PROP = '_gift_for_product';

  const int = (value, fallback = 0) => {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : fallback;
  };

  function isCart(value) {
    return !!(value && Array.isArray(value.items) && typeof value.item_count === 'number');
  }

  function cartOf(detail) {
    if (isCart(detail)) return detail;
    if (detail && isCart(detail.cart)) return detail.cart;
    if (detail && isCart(detail.cartData)) return detail.cartData;
    return null;
  }

  function sectionIds() {
    if (typeof window.sugarCartSectionIds === 'function') return window.sugarCartSectionIds();
    return ['sugar-cart-drawer', 'cart-icon-bubble'];
  }

  function sectionIdOf(el) {
    const section = el.closest('.shopify-section');
    return section && section.id ? section.id.replace(/^shopify-section-/, '') : null;
  }

  // Whether the subscriptions block in a section is set to subscribe
  function subscribed(sectionId) {
    if (!sectionId) return false;
    const state = window.subscriptionStateBySection && window.subscriptionStateBySection[sectionId];
    if (state) return state.type === 'subscribe';
    if (window.subscriptionState) return window.subscriptionState.type === 'subscribe';
    const section = document.getElementById('shopify-section-' + sectionId);
    return !!(section && section.querySelector('.sugar-subs__option--subscribe.is-selected'));
  }

  /* ------------------------------------------------------------ providers */

  function line(variantId, productId) {
    const id = int(variantId);
    if (!id || !productId) return null;
    return { id, quantity: 1, properties: { [PROP]: 'true', [FOR_PROP]: String(productId) } };
  }

  // Gifts the cart already holds for a product, so a second buy never doubles them
  function inCart(productId) {
    const cart = window.SugarCart ? window.SugarCart.peek() : null;
    const held = new Set();
    if (cart && Array.isArray(cart.items)) {
      cart.items.forEach((item) => {
        const p = item.properties || {};
        if (p[PROP] === 'true' && String(p[FOR_PROP] || '') === String(productId)) held.add(String(item.variant_id));
      });
    }
    return held;
  }

  function collectFrom(el) {
    // A gift block inside an offer card only counts while that offer is the selected tier
    const offer = el.closest('.offer');
    if (offer && !offer.classList.contains('is-selected')) return [];
    // A Perk's gift inside an offer card is added by the offer itself (offers.js giftLines)
    if (offer && el.matches('.offer__perk-gift')) return [];

    // A Gift List, Gift Grid or Featured Gift set inside a Subscriptions card follows that card:
    // it only counts while the shopper is subscribing
    const subsHost = el.closest('[data-sugar-subscriptions]');
    if (subsHost && el.matches('[data-gift-list], [data-featured-gift]')) {
      if (!subscribed(subsHost.dataset.sectionId || sectionIdOf(el))) return [];
    }

    const lines = [];
    if (el.matches('[data-gift-list]')) {
      const productId = el.dataset.qualifyingProductId;
      const held = inCart(productId);
      el.querySelectorAll('[data-gift-item][data-variant-id]').forEach((item) => {
        if (held.has(String(item.dataset.variantId))) return;
        const next = line(item.dataset.variantId, productId);
        if (next) lines.push(next);
      });
      return lines;
    }
    if (el.matches('[data-featured-gift]')) {
      if (el.dataset.autoAdd === 'false') return [];
      const productId = el.dataset.qualifyingProductId;
      if (inCart(productId).has(String(el.dataset.variantId))) return [];
      const next = line(el.dataset.variantId, productId);
      return next ? [next] : [];
    }
    // A gift a Perk or a Subscriptions card promises: only while the shopper is subscribing,
    // and an upsell only while its box is ticked.
    const subsRoot = el.closest('[data-sugar-subscriptions]');
    const sectionId = el.dataset.sectionId || (subsRoot && subsRoot.dataset.sectionId) || sectionIdOf(el);
    if (!subscribed(sectionId)) return [];
    if (el.dataset.giftType === 'upsell' && !el.classList.contains('is-checked')) return [];
    const productId = el.dataset.qualifyingProductId || (subsRoot && subsRoot.dataset.productId);
    if (inCart(productId).has(String(el.dataset.variantId))) return [];
    const next = line(el.dataset.variantId, productId);
    return next ? [next] : [];
  }

  function mount(el) {
    if (el.sugarExtras) return;
    el.setAttribute('data-cart-extras', '');
    el.sugarExtras = {
      collect: () => collectFrom(el),
      commit: () => refresh(),
    };
  }

  /* ------------------------------------------------------------ orphans */

  let busy = false;
  let dirty = false;
  let timer = null;

  function queueReconcile(cart) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      reconcile(cart).catch((e) => console.error('[Gift List] Sync error:', e));
    }, 40);
  }

  async function refresh() {
    if (!window.SugarCart) return;
    try {
      queueReconcile(await window.SugarCart.get({ fresh: true }));
    } catch (e) {
      /* the next cart event tries again */
    }
  }

  // A gift whose product has left the cart leaves with it
  async function reconcile(cart) {
    if (busy) {
      dirty = true;
      return;
    }
    if (!isCart(cart)) {
      cart = window.SugarCart ? await window.SugarCart.get({ fresh: true }) : null;
      if (!isCart(cart)) return;
    }
    const products = new Set();
    cart.items.forEach((item) => {
      if (!item.properties || item.properties[PROP] !== 'true') products.add(String(item.product_id));
    });
    const orphans = cart.items.filter((item) => {
      const p = item.properties;
      if (!p || p[PROP] !== 'true') return false;
      const productId = String(p[FOR_PROP] || '');
      return !productId || !products.has(productId);
    });
    if (!orphans.length) return;

    busy = true;
    try {
      const updates = {};
      orphans.forEach((item) => {
        updates[item.key] = 0;
      });
      const payload = { updates, sections: sectionIds(), sections_url: window.location.pathname };
      const result = window.SugarCart && typeof window.SugarCart.update === 'function'
        ? await window.SugarCart.update(payload)
        : await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload),
          }).then((res) => res.json());
      if (result && result.status) {
        console.error('[Gift List] Cart error:', result.description);
        return;
      }
      const cartData = window.SugarCart ? await window.SugarCart.get({ fresh: true }) : cartOf(result);
      if (cartData && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-list', cartData, sections: result && result.sections });
      }
      document.dispatchEvent(new CustomEvent('cart:update', { detail: result }));
    } finally {
      busy = false;
      if (dirty) {
        dirty = false;
        queueReconcile(null);
      }
    }
  }

  /* ------------------------------------------------------------ prices */

  function roundCents(cents, rounding) {
    if (!rounding || rounding === 'none') return cents;
    const dollars = Math.floor(cents / 100);
    switch (rounding) {
      case '95':
        return dollars * 100 + 95;
      case '99':
        return dollars * 100 + 99;
      case '00':
        return dollars * 100;
      default:
        return cents;
    }
  }

  function formatMoney(cents, format) {
    if (window.SugarPricing && typeof window.SugarPricing.formatMoney === 'function') {
      return window.SugarPricing.formatMoney(cents, format);
    }
    return (format || '${{amount}}').replace(/\{\{\s*amount\w*\s*\}\}/, (int(cents) / 100).toFixed(2));
  }

  // The struck-through prices in a Gift List or Gift Grid: the child renders them with the
  // theme's currency default, and the parent's rounding and currency choice are applied here.
  function paintPrices(scope) {
    const host = scope && scope.nodeType === 1 ? scope : document;
    const lists = host.matches && host.matches('[data-gift-list]') ? [host] : Array.from(host.querySelectorAll('[data-gift-list]'));
    lists.forEach((list) => {
      if (!list.dataset.moneyFormat) return;
      const rounding = list.dataset.priceRounding;
      const format = list.dataset.moneyFormat;
      let total = 0;
      list.querySelectorAll('[data-gift-item][data-gift-price]').forEach((item) => {
        const price = int(item.dataset.giftPrice);
        total += price;
        const text = (item.dataset.priceTemplate || '[price]').replace(/\[price\]/g, formatMoney(roundCents(price, rounding), format));
        item.querySelectorAll('[data-gift-price-text]').forEach((el) => {
          el.innerHTML = text;
        });
      });
      const totalEl = list.querySelector('[data-gift-list-total]');
      if (totalEl) totalEl.textContent = formatMoney(roundCents(total, rounding), format);
    });
  }

  /* ------------------------------------------------------------ boot */

  function init(scope) {
    const host = scope && scope.nodeType === 1 ? scope : document;
    const found = host.matches && host.matches(PROVIDERS) ? [host] : [];
    found.push(...Array.from(host.querySelectorAll(PROVIDERS)));
    found.forEach(mount);
    paintPrices(host);
  }

  function start() {
    init();
    // A shopper who comes back to the page may hold a gift whose product has gone
    refresh();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  document.addEventListener('cart:update', (e) => queueReconcile(cartOf(e.detail)));
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => queueReconcile(cartOf(event)));
  }

  // Providers that arrive later (a section re-rendered, a block added in the editor)
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1 && (node.matches(PROVIDERS) || node.querySelector(PROVIDERS))) init(node);
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', (e) => init(e.target));

  window.SugarGiftList = { init, refresh, paintPrices };
})();
